import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  Chip,
  CircularProgress,
  Alert,
  FormHelperText,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import useFetchObject from '../../api/useFetchObject';
import useFetchObjects from '../../api/useFetchObjects';
import useUpdate from '../../api/useUpdate';
import { useAuth } from '../../context/AuthContext';

const DepartmentEdit = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const token = user?.token;

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    directorate_id: '',
    manager_user_id: '',
  });

  // Validation state
  const [errors, setErrors] = useState({});
  
  // Fetch department data
  const { 
    data: department, 
    isLoading: loadingDepartment,
    isError: isDepartmentError,
    error: departmentError
  } = useFetchObject('departments', `departments/${id}`, id, token);
  
  // Fetch directorates
  const { 
    data: directoratesData, 
    isLoading: loadingDirectorates 
  } = useFetchObjects('directorates', {}, token);
  
  const directorates = Array.isArray(directoratesData) ? directoratesData : (directoratesData?.data || []);

  // Update mutation hook
  const { 
    handleUpdate, 
    loading, 
    success 
  } = useUpdate(
    'departments',
    token,
    '/departments',
    t('departments.update_success', 'Department updated successfully'),
    t('departments.update_error', 'Failed to update department')
  );

  // Set form data when department data is loaded
  useEffect(() => {
    if (department) {
      console.log('Department data loaded:', department);
      
      // Check if department.data exists (API standard response format)
      const departmentData = department.data || department;
      
      setFormData({
        name: departmentData.name || '',
        description: departmentData.description || '',
        directorate_id: departmentData.directorate_id || '',
        manager_user_id: departmentData.manager_user_id || '',
      });
      
      console.log('Form data set:', {
        name: departmentData.name || '',
        description: departmentData.description || '',
        directorate_id: departmentData.directorate_id || '',
        manager_user_id: departmentData.manager_user_id || '',
      });
    }
  }, [department]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('validation.required', 'This field is required');
    }
    
    if (!formData.directorate_id) {
      newErrors.directorate_id = t('validation.required', 'This field is required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    console.log('Submitting form data:', formData);
    
    // Submit form data
    handleUpdate(id, formData);
  };

  if (loadingDepartment) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isDepartmentError || !department) {
    return (
      <Box>
        <Typography variant="h5" component="h1" sx={{ mb: 2 }}>
          {t('departments.not_found', 'Department not found')}
        </Typography>
        <Alert severity="error" sx={{ mb: 3 }}>
          {departmentError?.message || t('common.error_occurred', 'An error occurred')}
        </Alert>
        <Button
          component={RouterLink}
          to="/departments"
          startIcon={<ArrowBackIcon />}
        >
          {t('common.back_to_list', 'Back to List')}
        </Button>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          component={RouterLink}
          to="/departments"
          startIcon={<ArrowBackIcon />}
          sx={{ mr: 2 }}
        >
          {t('common.back', 'Back')}
        </Button>
        <Typography variant="h4" component="h1" sx={{ flexGrow: 1 }}>
          {t('departments.edit_title', 'Edit Department')}
        </Typography>
        {department.is_active !== undefined && (
        <Chip 
            label={department.is_active ? t('common.active', 'Active') : t('common.inactive', 'Inactive')}
            color={department.is_active ? 'success' : 'error'}
        />
        )}
      </Box>

      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('departments.basic_info', 'Basic Information')}
          </Typography>
          
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="name"
                label={t('departments.name', 'Department Name')}
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="description"
                label={t('departments.description', 'Description')}
                value={formData.description}
                onChange={handleChange}
                multiline
                rows={3}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.directorate_id} required>
                <InputLabel id="directorate-label">
                  {t('departments.directorate', 'Directorate')}
                </InputLabel>
                <Select
                  labelId="directorate-label"
                  name="directorate_id"
                  label={t('departments.directorate', 'Directorate')}
                  value={formData.directorate_id}
                  onChange={handleChange}
                >
                  <MenuItem value="">
                    <em>{t('common.select', 'Select')}</em>
                  </MenuItem>
                  {loadingDirectorates ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} /> {t('common.loading', 'Loading...')}
                    </MenuItem>
                  ) : (
                    directorates.map((directorate) => (
                      <MenuItem key={directorate.id} value={directorate.id}>
                        {directorate.name}
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.directorate_id && (
                  <FormHelperText>{errors.directorate_id}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                disabled
                label={t('departments.head', 'Department Head')}
                value={department.manager?.username || department.manager?.email || ''}
                helperText={t('departments.cannot_change_manager', 'Manager cannot be changed directly')}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('departments.created_at', 'Created At')}
                value={department.created_at ? new Date(department.created_at).toLocaleDateString() : '-'}
                disabled
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label={t('departments.updated_at', 'Updated At')}
                value={department.updated_at ? new Date(department.updated_at).toLocaleDateString() : '-'}
                disabled
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 4 }} />

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
        <Button
          variant="outlined"
          component={RouterLink}
          to="/departments"
        >
          {t('common.cancel', 'Cancel')}
        </Button>
        <Button 
          variant="contained"
          type="submit"
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={24} />
          ) : (
            t('common.save', 'Save')
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default DepartmentEdit; 